// The Election Admin Suite's 9-dot app switcher in the tracker's header (2.0.3; 2.1.1: suiteOrigin and Poll Worker Management sign-ins; suite task 68).
//
// The switcher itself is the suite's shared script, public/switcher.js. The tracker serves its own
// copy of it, byte for byte the suite's file (test/unit.test.mjs fails if it changes from the
// recorded SHA-256), instead of loading script from the suite's address: a page that runs script
// from another server is only as safe as that server, and the tracker holds every office's plan.
// The only thing that leaves for the suite is the list of tools, GET <suite>/api/switcher, which
// the browser sends with the suite sign-in cookie (same site: every suite address is on
// electionadminsuite.com), so the list already matches the person; we pass the office being viewed.
//
// Where the list comes from: switcher.js asks the address given in data-api (and configure({api})),
// here the suite's address the Worker sends as suiteOrigin (from SUITE_URL; 2.1.1, so that people
// signed in with Poll Worker Management, whose sign-out link is relative, get it too). The script takes only an https electionadminsuite.com address or a local
// one, and so does suiteOriginFrom. With no such address (a local test copy without SUITE_URL) the
// button isn't shown, because it would only ever say "Your tools didn't load".
import React, { useEffect, useRef } from "react";
import { suiteOriginFrom } from "./switcherLink.js";

const TOOL_ID = "task-tracker";
const SCRIPT = "/switcher.js";

let scriptAdded = false;
let switcherHost = null; // the switcher's own element, moved between screens (it keeps its state)

// own: set for a person signed in another way than the suite (2.1.1: Poll Worker Management).
// They may have no suite cookie, so the suite would only show its sign-up note; signedIn: true has
// switcher.js list the suite's free tools instead, plus the entries given here: the tracker itself
// ("You are here") and, when known, the way back to their office's Poll Worker Management.
function settings(office, api, own) {
  const s = { tool: TOOL_ID, signedIn: own ? true : null, officeName: own ? own.officeName || "" : "", tools: null };
  // No suite address (production has no suite yet): api false, so switcher.js asks nobody and
  // shows only the tiles given here (election-admin-suite PR #155).
  s.api = api || false;
  // Only when known: with no office, switcher.js keeps the one in the page's ?office= (signed out).
  if (office) s.office = office;
  if (own) {
    s.tools = [{ id: TOOL_ID, name: "Election Task Tracker", url: `${location.origin}/${office ? `?office=${encodeURIComponent(office)}` : ""}` }];
    if (own.pwmUrl) s.tools.push({ id: "poll-worker-management", name: "Poll Worker Management", url: own.pwmUrl });
  }
  return s;
}

// Where the button goes. suite: the suite's address as the Worker sends it (suiteOrigin, from
// SUITE_URL). office: the office being viewed (session.org.id), or null when nobody is signed in
// (the switcher then shows its sign-up note). own: see settings(). color: the button's color (the
// dots take the text color of where they sit).
export default function SuiteSwitcher({ suite = null, office = null, own = null, color, accent }) {
  const spot = useRef(null);
  const origin = suiteOriginFrom([suite], location.origin);
  const ownKey = own ? `${own.officeName || ""}|${own.pwmUrl || ""}` : "";
  // 2.1.1: no suite address (production has no suite yet), but signed in with Poll Worker
  // Management: still shown, with just the tracker and the way back to PWM (see settings()), and
  // data-api="none" so nothing is fetched. Signed out with no suite: nothing to show.
  const shown = !!origin || !!(own && office);

  useEffect(() => {
    if (!shown || !spot.current) return;
    const cfg = settings(office, origin, own);
    if (window.SuiteSwitcher) window.SuiteSwitcher.configure(cfg);
    else window.SuiteSwitcherConfig = cfg;
    if (switcherHost) {
      // Already drawn on an earlier screen: move it here. Its shadow root and listeners go with it.
      spot.current.appendChild(switcherHost);
      return;
    }
    if (scriptAdded) return;
    scriptAdded = true;
    const s = document.createElement("script");
    s.src = SCRIPT;
    s.setAttribute("data-tool", TOOL_ID);
    s.setAttribute("data-api", origin || "none");
    s.onload = () => {
      switcherHost = document.querySelector("[data-suite-switcher] > .suite-switcher");
    };
    document.head.appendChild(s);
  }, [shown, origin, office, ownKey]);

  if (!shown) return null;
  const style = { display: "inline-flex", color };
  if (accent) style["--suite-switcher-accent"] = accent;
  // No React children inside the spot: the switcher puts its own element there.
  return <span ref={spot} data-suite-switcher="" style={style} />;
}
